import React, { useMemo, useState } from "react";
import { CheckCircle2 } from "lucide-react";

const WEEK_POINTS = [
  { day: "MON", value: 68, detail: "Reset energy", note: "Started the week steady with a calmer breath pattern." },
  { day: "TUE", value: 74, detail: "Clarity", note: "Focus stayed more consistent through the afternoon." },
  { day: "WED", value: 70, detail: "Recovery", note: "A shorter recovery window helped keep momentum." },
  { day: "THU", value: 82, detail: "Breakthrough", note: "Best regulation score after the midweek reset." },
  { day: "FRI", value: 88, detail: "Flow", note: "The strongest stability pattern of the week." },
  { day: "SAT", value: 58, detail: "Low energy", note: "Weekend rhythm dipped and recovery took longer." },
  { day: "SUN", value: 79, detail: "Rebound", note: "Breath steadied again after a slower morning." },
];

const KEY_PATTERNS = [
  { label: "Clarity on Tuesdays", value: 0.84 },
  { label: "Pattern Recognition", value: 0.63 },
  { label: "Vision to Execution", value: 0.44 },
];

const SVG_WIDTH = 320;
const SVG_HEIGHT = 150;
const TOP_PADDING = 16;
const BOTTOM_PADDING = 24;

function buildSmoothPath(points) {
  if (!points.length) {
    return "";
  }

  const [first, ...rest] = points;
  let path = `M ${first.x} ${first.y}`;

  rest.forEach((point, index) => {
    const prev = points[index];
    const controlX = (prev.x + point.x) / 2;
    path += ` C ${controlX} ${prev.y}, ${controlX} ${point.y}, ${point.x} ${point.y}`;
  });

  return path;
}

export const Graph = () => {
  const [activeDay, setActiveDay] = useState("FRI");

  const activePoint = WEEK_POINTS.find((point) => point.day === activeDay) || WEEK_POINTS[4];

  const chartPoints = useMemo(() => {
    const usableHeight = SVG_HEIGHT - TOP_PADDING - BOTTOM_PADDING;
    const stepX = SVG_WIDTH / (WEEK_POINTS.length - 1);

    return WEEK_POINTS.map((point, index) => ({
      ...point,
      x: index * stepX,
      y: TOP_PADDING + (1 - point.value / 100) * usableHeight,
    }));
  }, []);

  const linePath = useMemo(() => buildSmoothPath(chartPoints), [chartPoints]);
  const areaPath = useMemo(() => {
    if (!chartPoints.length) {
      return "";
    }

    const start = chartPoints[0];
    const end = chartPoints[chartPoints.length - 1];
    return `${linePath} L ${end.x} ${SVG_HEIGHT - 4} L ${start.x} ${SVG_HEIGHT - 4} Z`;
  }, [chartPoints, linePath]);

  return (
    <div data-testid="live-graph" className="relative z-10 w-full">
      <div
        className="overflow-hidden rounded-[2rem] px-5 py-5"
        style={{
          background: "linear-gradient(180deg, rgba(24,16,10,0.96) 0%, rgba(11,10,9,0.98) 76%)",
          border: "1px solid rgba(255,255,255,0.05)",
          boxShadow: "0 28px 64px rgba(0,0,0,0.28), inset 0 1px 0 rgba(255,255,255,0.04)",
        }}
      >
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-[11px] uppercase tracking-[0.36em] text-[#9A8468]">Stability Index</h3>
            <div className="mt-3 flex items-end gap-2">
              <span className="text-6xl font-light leading-none text-[#F7EEDA]">{activePoint.value}</span>
              <span className="pb-2 text-xl text-[#B8AA93]">%</span>
            </div>
            <p className="mt-2 text-[11px] uppercase tracking-[0.22em] text-[#84735C]">
              +12% from last cycle
            </p>
          </div>
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-[0.34em] text-[#8D785E]">{activePoint.detail}</p>
            <p className="mt-3 max-w-[9rem] text-xs leading-5 text-[#B9AE9E]">{activePoint.note}</p>
          </div>
        </div>

        <div
          className="relative mt-7 overflow-hidden rounded-[1.6rem] px-3 pt-5 pb-3"
          style={{
            background: "linear-gradient(180deg, rgba(12,10,9,0.55) 0%, rgba(8,8,8,0.12) 100%)",
          }}
        >
          <div
            aria-hidden="true"
            className="pointer-events-none absolute left-1/2 top-10 h-28 w-40 -translate-x-1/2 rounded-full"
            style={{
              background: "radial-gradient(circle, rgba(245,158,11,0.18) 0%, rgba(245,158,11,0.02) 68%, transparent 100%)",
              filter: "blur(18px)",
            }}
          />
          <svg
            viewBox={`0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`}
            className="relative z-10 w-full"
            style={{ display: "block", height: "190px" }}
            preserveAspectRatio="none"
          >
            <defs>
              <linearGradient id="weekLine" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#C88B12" />
                <stop offset="48%" stopColor="#F0C35A" />
                <stop offset="100%" stopColor="#F4D17A" />
              </linearGradient>
              <linearGradient id="weekArea" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="rgba(245,195,90,0.22)" />
                <stop offset="68%" stopColor="rgba(245,195,90,0.04)" />
                <stop offset="100%" stopColor="rgba(245,195,90,0)" />
              </linearGradient>
              <filter id="lineGlow">
                <feGaussianBlur stdDeviation="5" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>

            <path d={areaPath} fill="url(#weekArea)" />
            <path
              d={linePath}
              fill="none"
              stroke="url(#weekLine)"
              strokeWidth="2.6"
              strokeLinecap="round"
              filter="url(#lineGlow)"
            />

            {chartPoints.map((point) => {
              const isActive = point.day === activeDay;

              return (
                <g
                  key={point.day}
                  onClick={() => setActiveDay(point.day)}
                  style={{ cursor: "pointer" }}
                >
                  <circle
                    cx={point.x}
                    cy={point.y}
                    r={isActive ? 4.2 : 3}
                    fill={isActive ? "#F8E7BE" : "#D7AF53"}
                    stroke={isActive ? "#FFF5DE" : "rgba(255,245,222,0.32)"}
                    strokeWidth={isActive ? 1.6 : 1}
                  />
                  {isActive && (
                    <circle
                      cx={point.x}
                      cy={point.y}
                      r={11}
                      fill="rgba(245,195,90,0.12)"
                    />
                  )}
                </g>
              );
            })}
          </svg>

          <div className="mt-2 flex items-center justify-between px-1">
            {WEEK_POINTS.map((point) => (
              <button
                key={point.day}
                type="button"
                onClick={() => setActiveDay(point.day)}
                className="text-[10px] uppercase tracking-[0.22em]"
                style={{
                  color: point.day === activeDay ? "#F7EEDA" : "#78664F",
                  transition: "color 0.2s ease, transform 0.2s ease",
                  transform: point.day === activeDay ? "translateY(-1px)" : "translateY(0)",
                }}
              >
                {point.day}
              </button>
            ))}
          </div>
        </div>

        <div
          className="mt-4 flex items-center gap-3 rounded-[1.5rem] px-4 py-3"
          style={{
            background: "linear-gradient(180deg, rgba(37,25,16,0.8) 0%, rgba(24,17,12,0.75) 100%)",
            border: "1px solid rgba(255,255,255,0.04)",
          }}
        >
          <CheckCircle2 size={18} style={{ color: "#F7EEDA" }} />
          <span className="text-4xl font-light leading-none text-[#F7EEDA]">42</span>
          <span className="text-[11px] uppercase tracking-[0.24em] text-[#87745B]">Tasks completed</span>
        </div>

        <div
          className="mt-4 rounded-[1.8rem] px-5 py-5"
          style={{
            background: "linear-gradient(180deg, rgba(9,9,9,0.98) 0%, rgba(13,13,13,0.96) 100%)",
            border: "1px solid rgba(255,255,255,0.04)",
          }}
        >
          <p className="text-[10px] uppercase tracking-[0.34em] text-[#72614A]">Key Patterns</p>
          <div className="mt-5 space-y-5">
            {KEY_PATTERNS.map((pattern) => (
              <div key={pattern.label}>
                <div className="mb-2 flex items-center justify-between gap-4">
                  <span className="text-sm text-[#8F857A]">{pattern.label}</span>
                  <span className="text-xs text-[#C9BDA8]">{Math.round(pattern.value * 100)}%</span>
                </div>
                <div
                  className="h-1.5 rounded-full"
                  style={{ background: "rgba(122,101,70,0.42)" }}
                >
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: `${pattern.value * 100}%`,
                      background: "linear-gradient(90deg, #F6EEE0 0%, #C9B188 100%)",
                      boxShadow: "0 0 14px rgba(246,238,224,0.18)",
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Graph;
